export class LightGallery extends HTMLElement {
    static get observedAttributes() {
        return ['referential', 'referential-text'];
    }

    constructor() {
        super();
        this.items = [];
        this.currentIndex = 0;
        this.isAnimating = false;
        this.observer = null; // Observer para detectar cambios dinámicos

        // Configuración de thumbnails
        this.showThumbnails = this.hasAttribute('thumbnails');

        // Inyectar estilos CSS de View Transition en el documento (una sola vez)
        if (!document.getElementById('lightgallery-view-transition-styles')) {
            const style = document.createElement('style');
            style.id = 'lightgallery-view-transition-styles';
            style.textContent = `
                /* View Transition styles for LightGallery */
                ::view-transition-group(gallery-img-*) {
                    animation-duration: 0.4s;
                    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                ::view-transition-old(gallery-img-*),
                ::view-transition-new(gallery-img-*) {
                    height: 100%;
                    width: 100%;
                    object-fit: contain;
                }
            `;
            document.head.appendChild(style);
        }

        // Overlay (contenedor principal sin background)
        this.overlay = document.createElement('div');
        this.overlay.style.cssText = `
            position: fixed; top:0; left:0; width:100%; height:100%;
            display:flex; justify-content:center; align-items:center;
            z-index:9999; opacity:0; pointer-events:none;
            flex-direction: column;
        `;
        document.body.appendChild(this.overlay);

        // Backdrop (fondo oscuro separado para mejor control)
        this.backdrop = document.createElement('div');
        this.backdrop.style.cssText = `
            position: absolute; top:0; left:0; width:100%; height:100%;
            background: rgba(55, 65, 81, 1);
            z-index: -1;
        `;
        this.overlay.appendChild(this.backdrop);

        // Contenedor principal
        this.mainContainer = document.createElement('div');
        this.mainContainer.style.cssText = `
            position: relative;
            width: 90%;
            max-width: 1200px;
            height: 94%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
        `;
        this.mainContainer.addEventListener('click', e => e.stopPropagation());
        this.overlay.appendChild(this.mainContainer);

        // Botón de cierre
        this.closeBtn = document.createElement('button');
        this.closeBtn.innerHTML = '<span style="width:24px; height:24px; display:flex; align-items:center; justify-content:center; font-size:24px; line-height:1;">✕</span><span style="font-size:16px;">Cerrar</span>';
        this.closeBtn.style.cssText = `
            position: relative;
            background: rgba(0,0,0,0.3); border:none; 
            color: white; cursor: pointer; z-index:10001;
            display: flex; align-items: center; gap: 8px;
            padding: 8px 12px;
            border-radius: 4px;
            backdrop-filter: blur(4px);
            transition: opacity 0.25s ease;
        `;
        this.closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.close();
        });

        // Contenedor de slider con navegación
        this.sliderWrapper = document.createElement('div');
        this.sliderWrapper.style.cssText = `
            position: relative;
            width: 100%;
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            max-height: calc(100% - 60px);
        `;
        this.mainContainer.appendChild(this.sliderWrapper);

        // Slider
        this.slider = document.createElement('div');
        this.slider.style.cssText = `
            position: relative;
            width: 100%;
            height: 100%;
            overflow: hidden;
            display: flex;
            user-select: none;
            -webkit-user-select: none;
            border-radius: 8px;
        `;
        this.sliderWrapper.appendChild(this.slider);

        // Track (Riel para las imágenes) - SIN transición para cambios instantáneos
        this.track = document.createElement('div');
        this.track.style.cssText = `
            display: flex;
            width: 100%;
            height: 100%;
        `;
        this.slider.appendChild(this.track);

        // Botones de navegación
        this.prevBtn = document.createElement('button');
        this.nextBtn = document.createElement('button');
        [this.prevBtn, this.nextBtn].forEach(btn=>{
            btn.style.cssText = `
                position:absolute;
                top:50%;
                transform:translateY(-50%);
                border:none;
                width: 48px;
                height: 48px;
                border-radius: 50%;
                cursor:pointer;
                font-size:1.5rem;
                z-index:10000;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #ffffff;
            `;
        });
        this.prevBtn.innerHTML = '‹';
        this.nextBtn.innerHTML = '›';
        this.prevBtn.style.left='-60px';
        this.nextBtn.style.right='-60px';


        this.sliderWrapper.appendChild(this.prevBtn);
        this.sliderWrapper.appendChild(this.nextBtn);

        // Botón de cierre fijo en el sliderWrapper (no se mueve entre slides)
        this.sliderWrapper.appendChild(this.closeBtn);

        // Etiqueta referencial (fija al fondo de la imagen)
        this.referentialLabel = document.createElement('div');
        this.referentialLabel.style.cssText = `
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(0, 0, 0, 0.45);
            color: white;
            text-align: end;
            padding: 8px 24px;
            font-size: 14px;
            display: none;
            border-radius: 0 0 8px 8px;
            pointer-events: none;
            z-index: 5;
            letter-spacing: 0.02em;
            transition: opacity 0.25s ease;
        `;
        this.slider.appendChild(this.referentialLabel);

        // Contenedor de controles (flechas + contador) para mobile
        this.controlsContainer = document.createElement('div');
        this.controlsContainer.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 20px;
            margin-top: 15px;
            width: 100%;
        `;
        this.mainContainer.appendChild(this.controlsContainer);

        // Contador
        this.counter = document.createElement('div');
        this.counter.style.cssText = `
            color: white;
            font-size: 1rem;
            text-align: center;
        `;
        this.controlsContainer.appendChild(this.counter);

        // Thumbnails (si están habilitados)
        if(this.showThumbnails){
            this.thumbContainer = document.createElement('div');
            this.thumbContainer.style.cssText = `
                display:flex; gap:8px; overflow-x:auto; margin-top:15px; max-width:100%;
                padding: 10px 0;
            `;
            this.mainContainer.appendChild(this.thumbContainer);
        }

        // Responsive styles
        this.updateResponsiveStyles();
        window.addEventListener('resize', ()=> this.updateResponsiveStyles());

        // Cerrar overlay click en fondo
        this.overlay.addEventListener('click', e=>{
            if(e.target===this.overlay) {
                this.close();
                e.stopPropagation();
            }
        });

        // Keyboard
        document.addEventListener('keydown', e=>{
            if(e.key==='Escape') this.close();
            if(e.key==='ArrowLeft') this.prev();
            if(e.key==='ArrowRight') this.next();
        });

        // Swipe / Drag
        this.startX = 0;
        this.isDragging = false;
        const dragStart = e => {
            // No prevenir default si el target es el botón de cerrar o un control
            if(e.target.closest('button')) return;
            
            this.isDragging=true;
            this.startX=e.clientX||e.touches[0].clientX;
            const currentSlide = this.track.children[this.currentIndex];
            const slideImg = currentSlide?.querySelector('img, iframe');
            if(slideImg){
                slideImg.style.transition = 'none';
            }
        };
        const dragMove = e => {
            if(!this.isDragging) return;
            e.preventDefault();
            const currentSlide = this.slider.firstChild;
            if(currentSlide){
                const dx=(e.clientX||e.touches[0].clientX)-this.startX;
                currentSlide.style.transform=`translateX(${dx}px)`;
            }
        };
        const dragEnd = e => {
            if(!this.isDragging) return;
            const dx=(e.clientX||e.changedTouches[0].clientX)-this.startX;
            this.endDrag(dx);
        };
        ['mousedown','touchstart'].forEach(ev=>this.slider.addEventListener(ev, dragStart));
        ['mousemove','touchmove'].forEach(ev=>this.slider.addEventListener(ev, dragMove));
        ['mouseup','mouseleave','touchend'].forEach(ev=>this.slider.addEventListener(ev, dragEnd));
    }

    updateResponsiveStyles(){
        const isMobile = window.innerWidth <= 768;

        if(isMobile){
            // Mobile styles - Flechas en los extremos, contador en el medio
            this.prevBtn.style.position='relative';
            this.nextBtn.style.position='relative';
            this.prevBtn.style.left='auto';
            this.nextBtn.style.right='auto';
            this.prevBtn.style.top='auto';
            this.nextBtn.style.top='auto';
            this.prevBtn.style.bottom='auto';
            this.nextBtn.style.bottom='auto';
            this.prevBtn.style.transform='none';
            this.nextBtn.style.transform='none';
            this.nextBtn.style.width='48px';
            this.nextBtn.style.height='48px';
            this.prevBtn.style.fontSize='1.5rem';
            this.nextBtn.style.fontSize='1.5rem';

            // Reorganizar controles en mobile: prevBtn - counter - nextBtn en una línea
            this.controlsContainer.style.display='flex';
            this.controlsContainer.style.flexDirection='row';
            this.controlsContainer.style.alignItems='center';
            this.controlsContainer.style.justifyContent='space-between';
            this.controlsContainer.style.gap='20px';
            this.controlsContainer.style.marginTop='20px';
            this.controlsContainer.style.width='100%';
            this.controlsContainer.style.maxWidth='400px';

            // Reorganizar el DOM: prevBtn - counter - nextBtn
            this.controlsContainer.innerHTML = '';
            this.controlsContainer.appendChild(this.prevBtn);
            this.controlsContainer.appendChild(this.counter);
            this.controlsContainer.appendChild(this.nextBtn);

            this.counter.style.fontSize='1rem';
            this.counter.style.minWidth='60px';
            this.counter.style.flex='0 0 auto';

            this.mainContainer.style.width='100%';
            this.mainContainer.style.height='100%';
            this.mainContainer.style.padding='0';
            this.mainContainer.style.justifyContent = 'center';
            
            // Homologación: El botón de cierre también va sobre la imagen en mobile
            this.closeBtn.style.position = 'absolute';
            this.closeBtn.style.top = '10px';
            this.closeBtn.style.right = '10px';
            this.closeBtn.style.left = 'auto';
            this.closeBtn.style.marginBottom = '0';
            this.closeBtn.style.background = 'rgba(0,0,0,0.5)';
            this.closeBtn.style.padding = '5px 10px'; // Un poco más pequeño
            this.closeBtn.style.fontSize = '14px';
            this.closeBtn.style.zIndex = '10002';

            this.sliderWrapper.style.maxHeight='350px'; // Reducido de 500px
            this.sliderWrapper.style.height='auto';
            this.sliderWrapper.style.flex='0 0 auto';
            this.sliderWrapper.style.marginTop='0';
            this.slider.style.height = 'auto';
            this.track.style.height = 'auto';

            // Ocultar thumbnails en mobile
            if(this.showThumbnails){
                this.thumbContainer.style.display='none';
            }
        } else {
            // Desktop styles - Flechas a los lados
            this.prevBtn.style.position='absolute';
            this.nextBtn.style.position='absolute';
            this.prevBtn.style.left='-60px';
            this.nextBtn.style.right='-60px';
            this.prevBtn.style.width='48px';
            this.prevBtn.style.height='48px';
            this.nextBtn.style.width='48px';
            this.nextBtn.style.height='48px';
            this.prevBtn.style.fontSize='1.5rem';
            this.nextBtn.style.fontSize='1.5rem';
            this.prevBtn.style.top='50%';
            this.nextBtn.style.top='50%';
            this.prevBtn.style.bottom='auto';
            this.nextBtn.style.bottom='auto';
            this.prevBtn.style.transform='translateY(-50%)';
            this.nextBtn.style.transform='translateY(-50%)';

            // En desktop, las flechas están en sliderWrapper
            if(this.prevBtn.parentElement !== this.sliderWrapper){
                this.sliderWrapper.appendChild(this.prevBtn);
                this.sliderWrapper.appendChild(this.nextBtn);
            }

            // Contador debajo en desktop
            this.controlsContainer.style.display='flex';
            this.controlsContainer.style.flexDirection='row';
            this.controlsContainer.style.justifyContent='center';
            this.controlsContainer.style.marginTop='15px';
            this.controlsContainer.innerHTML='';
            this.controlsContainer.appendChild(this.counter);

            this.counter.style.fontSize='1rem';
            this.counter.style.flex='none';

            this.mainContainer.style.width='90%';
            this.mainContainer.style.height='auto';
            this.mainContainer.style.maxHeight='96%';
            this.mainContainer.style.padding='0';
            this.mainContainer.style.justifyContent = 'center';
            this.controlsContainer.style.maxWidth = 'none';
            this.controlsContainer.style.width = '100%';
            this.controlsContainer.style.marginTop = '10px';
            
            this.sliderWrapper.style.maxHeight = 'none';
            this.sliderWrapper.style.flex = '0 1 auto';
            this.sliderWrapper.style.height = 'auto';
            this.sliderWrapper.style.width = '100%';
            this.sliderWrapper.style.minHeight='0';
            this.sliderWrapper.style.marginTop='0';

            this.slider.style.height = 'auto';
            this.slider.style.maxHeight = 'calc(96vh - 160px)';
            this.track.style.height = 'auto';
            
            // El botón de cierre se posiciona sobre la imagen (fijo en sliderWrapper)
            this.closeBtn.style.position = 'absolute';
            this.closeBtn.style.top = '10px';
            this.closeBtn.style.right = '10px';
            this.closeBtn.style.left = 'auto';
            this.closeBtn.style.marginBottom = '0';
            this.closeBtn.style.background = 'rgba(0,0,0,0.3)';
            this.closeBtn.style.padding = '8px 12px';
            this.closeBtn.style.zIndex = '10002';

            // Mostrar thumbnails en desktop
            if(this.showThumbnails){
                this.thumbContainer.style.display='flex';
            }
        }
    }

    connectedCallback(){
        this.refreshItems();
        this.setupMutationObserver();
        this.prevBtn.addEventListener('click', e=>{ e.stopPropagation(); this.prev(); });
        this.nextBtn.addEventListener('click', e=>{ e.stopPropagation(); this.next(); });
    }

    disconnectedCallback(){
        // Limpiar el observer cuando el componente se desconecta
        if(this.observer){
            this.observer.disconnect();
        }
    }

    setupMutationObserver(){
        // Observer para detectar cambios en los hijos del componente
        this.observer = new MutationObserver((mutations) => {
            // Ignorar cambios en el atributo style para evitar loops
            const shouldRefresh = mutations.some(mutation =>
                mutation.type === 'childList' ||
                (mutation.type === 'attributes' &&
                 (mutation.attributeName === 'data-src' || mutation.attributeName === 'data-type') &&
                 mutation.attributeName !== 'style')
            );

            if(shouldRefresh){
                this.refreshItems();
            }
        });

        // Configurar el observer
        this.observer.observe(this, {
            childList: true,        // Detectar añadir/eliminar hijos
            subtree: true,          // Observar todo el árbol de descendientes
            attributes: true,       // Detectar cambios en atributos
            attributeFilter: ['data-src', 'data-type'] // Solo observar estos atributos
        });
    }

    refreshItems(){
        this.items = Array.from(this.querySelectorAll('[data-src]'));

        // Proteger el label de ser eliminado al limpiar el track
        if (this.track.contains(this.referentialLabel)) {
            this.slider.appendChild(this.referentialLabel);
        }
        this.track.innerHTML = ''; // Limpiar riel

        this.items.forEach((item, index) => {
            // Inicializar activadores en la página
            if(!item.dataset.lightgalleryInit) {
                item.style.cursor = 'pointer';
                item.dataset.lightgalleryInit = 'true';
            }

            if (item._lightgalleryHandler) item.removeEventListener('click', item._lightgalleryHandler);
            const clickHandler = (e) => { e.stopPropagation(); this.open(index); };
            item.addEventListener('click', clickHandler);
            item._lightgalleryHandler = clickHandler;

            // Crear el slide en el riel
            const slide = document.createElement('div');
            const isMobile = window.innerWidth <= 768;
            slide.style.cssText = `
                flex: 0 0 100%;
                width: 100%;
                height: ${isMobile ? '100%' : 'auto'};
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
            `;
            
            // Contenedor interno para que el botón de cerrar quede sobre la imagen
            const contentWrapper = document.createElement('div');
            contentWrapper.className = 'lg-content-wrapper';
            contentWrapper.style.cssText = `
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                width: fit-content;
                height: fit-content;
                max-width: 100%;
            `;
            slide.appendChild(contentWrapper);
            this.track.appendChild(slide);
        });

        if(this.observer){
            this.observer.disconnect();
            this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-src', 'data-type'] });
        }
    }

    open(index){
        this.currentIndex=index;
        this.updateCounter();
        this.updateThumbs();

        // Ocultar controles de forma instantánea ANTES de cualquier animación
        this.closeBtn.style.transition = 'none';
        this.referentialLabel.style.transition = 'none';
        this.closeBtn.style.opacity = '0';
        this.closeBtn.style.pointerEvents = 'none';
        this.referentialLabel.style.opacity = '0';

        // Obtener el elemento origen (la imagen que se clickeó)
        const sourceElement = this.items[index];
        const transitionName = `gallery-img-${Date.now()}`;

        // Función para abrir la galería
        const openGallery = () => {
            // IMPORTANTE: Deshabilitar la transición del track al abrir
            // para que no haya slide desde el inicio
            this.track.style.transition = 'none';

            // Cargar contenido de los slides
            this.items.forEach((item, i) => {
                const slide = this.track.children[i];
                const container = slide.querySelector('.lg-content-wrapper');
                if (container.children.length === 0) {
                    const type = item.dataset.type || 'image';
                    let content;
                    if(type === 'image') {
                        content = document.createElement('img');
                        content.src = item.dataset.src;
                        content.style.cssText = 'max-width:100%; max-height:calc(96vh - 160px); object-fit:contain; display:block;';
                    } else {
                        const isDirectVideoFile = /\.(mp4|webm|ogg|ogv|mov)(\?|#|$)/i.test(item.dataset.src);
                        if (isDirectVideoFile) {
                            // Archivo de video directo (ej: CloudFront S3): usar <video> para poder pausar nativamente
                            content = document.createElement('video');
                            content.src = item.dataset.src;
                            content.controls = true;
                            content.style.cssText = 'max-width:100%; max-height:calc(96vh - 160px); display:block; border-radius:4px;';
                        } else {
                            // Embed externo (YouTube, Vimeo, etc.): usar <iframe> con postMessage
                            content = document.createElement('iframe');
                            let iframeSrc = item.dataset.src;
                            try {
                                const url = new URL(iframeSrc);
                                if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be')) {
                                    url.searchParams.set('enablejsapi', '1');
                                    iframeSrc = url.toString();
                                }
                            } catch (_) { /* URL relativa u otro formato */ }
                            content.src = iframeSrc;
                            content.style.cssText = 'width:100%; aspect-ratio:16/9; max-height:calc(96vh - 160px); border:none; display:block;';
                            content.allow = 'autoplay; fullscreen';
                        }
                        // El wrapper debe ser full-width para que tenga referencia correcta
                        container.style.width = '100%';
                        container.style.maxWidth = '100%';
                    }
                    content.addEventListener('click', e => e.stopPropagation());
                    container.appendChild(content);
                }

                // Asignar view-transition-name a la imagen activa (cualquier índice)
                if(i === index) {
                    const existingImage = container.querySelector('img');
                    if(existingImage) {
                        existingImage.style.viewTransitionName = transitionName;
                    }
                }
            });

            if (this.showThumbnails && this.thumbContainer.children.length === 0) {
                this.items.forEach((item, i) => {
                    const thumb = document.createElement('img');
                    thumb.src = item.dataset.src;
                    thumb.style.cssText = `width:80px; height:60px; object-fit:cover; cursor:pointer; border-radius:4px; opacity:${i===index?1:0.5}; transition:0.3s; border:2px solid ${i===index?'white':'transparent'};`;
                    thumb.addEventListener('click', (e) => { e.stopPropagation(); this.goTo(i); });
                    this.thumbContainer.appendChild(thumb);
                });
            }

            this.snapToCurrent();
            this.updateReferentialLabel();
            this.overlay.style.opacity='1';
            this.overlay.style.pointerEvents='auto';


            // IMPORTANTE: Habilitar la transición del track DESPUÉS de abrir
            // para que funcione en navegación (swipe/drag/next/prev)
            // Usamos setTimeout para que se aplique después del primer render
            setTimeout(() => {
                this.track.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
            }, 50);
        };

        // Usar View Transition API para TODAS las imágenes
        if (document.startViewTransition && !this.prefersReducedMotion()) {
            // Limpiar cualquier view-transition-name residual de transiciones anteriores
            this.items.forEach(item => {
                item.style.viewTransitionName = '';
            });
            this.track.querySelectorAll('img').forEach(img => {
                img.style.viewTransitionName = '';
            });

            // IMPORTANTE: Asignar view-transition-name al elemento origen ANTES de la transición
            // Necesitamos forzar un reflow para asegurar que el navegador capture el estado inicial
            sourceElement.style.viewTransitionName = transitionName;

            // Forzar reflow
            sourceElement.offsetHeight;

            const transition = document.startViewTransition(() => {
                // Durante la transición, el elemento origen está oculto (opacity 0 por el overlay)
                // y la imagen del lightbox se muestra con el mismo view-transition-name
                openGallery();

                // Remover el view-transition-name del origen después de abrir
                // para que no interfiera con el estado del overlay
                sourceElement.style.viewTransitionName = '';
            });

            // Limpiar después de que la transición termine
            transition.finished
                .then(() => {
                    const activeSlide = this.track.children[index];
                    const activeImage = activeSlide?.querySelector('img');
                    if(activeImage) {
                        activeImage.style.viewTransitionName = '';
                    }
                    this._showControls();
                })
                .catch(() => {
                    // Limpiar incluso si la transición falla
                    sourceElement.style.viewTransitionName = '';
                    this._showControls();
                });
        } else {
            // Fallback sin View Transition
            openGallery();
            this._showControls();
        }
    }

    pauseCurrentVideo() {
        const slide = this.track.children[this.currentIndex];
        if (!slide) return;

        // 1. Elemento <video> directo en el DOM (archivos MP4/WebM de CloudFront, etc.)
        const video = slide.querySelector('video');
        if (video) {
            video.pause();
            return;
        }

        // 2. iframe — intentar same-origin primero
        const iframe = slide.querySelector('iframe');
        if (!iframe) return;

        try {
            const iframeVideo = iframe.contentDocument?.querySelector('video');
            if (iframeVideo) { iframeVideo.pause(); return; }
        } catch (_) { /* cross-origin: ignorar */ }

        // 3. YouTube (requiere enablejsapi=1, añadido al crear el iframe)
        iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            '*'
        );

        // 4. Vimeo
        iframe.contentWindow?.postMessage(
            JSON.stringify({ method: 'pause' }),
            '*'
        );
    }

    goTo(index) {
        this.pauseCurrentVideo();
        this.currentIndex = index;
        this.snapToCurrent();
        this.updateThumbs();
        this.updateCounter();
        this.updateReferentialLabel();
    }

    snapToCurrent() {
        this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;
    }

    updateCounter(){
        this.counter.textContent = `${this.currentIndex + 1}/${this.items.length}`;
    }

    updateThumbs(){
        if(!this.showThumbnails || !this.thumbContainer.children.length) return;
        Array.from(this.thumbContainer.children).forEach((thumb,i)=>{
            thumb.style.opacity = i===this.currentIndex? '1':'0.5';
            thumb.style.border = `2px solid ${i===this.currentIndex?'white':'transparent'}`;
        });
    }

    _showControls() {
        this.closeBtn.style.transition = 'opacity 0.25s ease';
        this.referentialLabel.style.transition = 'opacity 0.25s ease';
        this.closeBtn.style.opacity = '1';
        this.closeBtn.style.pointerEvents = '';
        this.referentialLabel.style.opacity = '1';
    }

    updateReferentialLabel(){
        const item = this.items[this.currentIndex];
        const isImage = (item?.dataset.type || 'image') === 'image';
        if (this.hasAttribute('referential') && isImage) {
            this.referentialLabel.textContent = this.getAttribute('referential-text') || 'Foto referencial';
            this.referentialLabel.style.display = 'block';
        } else {
            this.referentialLabel.style.display = 'none';
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return;
        if (name === 'referential' || name === 'referential-text') {
            this.updateReferentialLabel();
        }
    }

    prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    close(){
        this.pauseCurrentVideo();

        // Deshabilitar la transición del track para la próxima apertura
        this.track.style.transition = 'none';

        // Usar View Transition API para el cierre con cualquier imagen
        if (document.startViewTransition && !this.prefersReducedMotion()) {
            const sourceElement = this.items[this.currentIndex];
            const transitionName = `gallery-img-close-${Date.now()}`;

            const activeSlide = this.track.children[this.currentIndex];
            const activeImage = activeSlide?.querySelector('img');

            // Solo hacer view transition si hay una imagen activa
            if(activeImage && sourceElement) {
                // Limpiar cualquier view-transition-name existente
                this.track.querySelectorAll('img').forEach(img => {
                    img.style.viewTransitionName = '';
                });

                // Solo asignar el nombre a la imagen del lightbox primero
                activeImage.style.viewTransitionName = transitionName;

                // Forzar reflow
                activeImage.offsetHeight;

                // Dejar que View Transition haga su magia automáticamente
                const transition = document.startViewTransition(() => {
                    // DENTRO del callback:
                    // 1. Remover el nombre del lightbox
                    activeImage.style.viewTransitionName = '';

                    // 2. Asignar el nombre a la imagen original (para la transición de salida)
                    sourceElement.style.viewTransitionName = transitionName;

                    // 3. Ocultar el overlay
                    this.overlay.style.opacity = '0';
                    this.overlay.style.pointerEvents = 'none';
                });

                // Limpiar después de la transición
                transition.finished.then(() => {
                    sourceElement.style.viewTransitionName = '';
                    activeImage.style.viewTransitionName = '';
                }).catch(() => {
                    // Limpiar incluso si falla
                    this.overlay.style.opacity = '0';
                    this.overlay.style.pointerEvents = 'none';
                    sourceElement.style.viewTransitionName = '';
                    if(activeImage) {
                        activeImage.style.viewTransitionName = '';
                    }
                });
            } else {
                // Si no hay imagen, cerrar sin transición
                this.overlay.style.opacity='0';
                this.overlay.style.pointerEvents='none';
            }
        } else {
            // Fallback sin View Transition
            this.overlay.style.opacity='0';
            this.overlay.style.pointerEvents='none';
        }
    }

    prev(){
        const newIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
        this.goTo(newIndex);
    }

    next(){
        const newIndex = (this.currentIndex + 1) % this.items.length;
        this.goTo(newIndex);
    }

    endDrag(dx){
        this.isDragging=false;
        if(Math.abs(dx) > 50) {
            dx > 0 ? this.prev() : this.next();
        } else {
            this.snapToCurrent();
        }
    }

    preload(index){
        index=(index+this.items.length)%this.items.length;
        const item=this.items[index];
        if(item.dataset.type==='image'){
            const img=new Image();
            img.src=item.dataset.src;
        }
    }
}
